import { delay } from "@utils/time-utils";
import { useCallback } from "react";

// Rate limiting: 100ms delay = max 10 requests/sec to avoid overwhelming the API
const RATE_LIMIT_DELAY_MS = 100;

/**
 * Response from a paginated API endpoint with cursor-based pagination
 */
type PaginatedApiResponse<TData> = {
  /** Array of data items in the current page */
  data: TData[];
} & Record<string, unknown>;

/**
 * Parameters with optional cursor for pagination
 */
type PaginatedParams<TParams> = TParams & Record<string, string | undefined>;

/**
 * Function signature for fetching a single page from a paginated API
 */
type FetchPageFunction<TParams, TData> = (
  params: PaginatedParams<TParams>
) => Promise<PaginatedApiResponse<TData>>;

/**
 * Function signature for transforming raw API data to desired output format
 */
type TransformFunction<TData, TResult> = (rawData: TData[]) => TResult[];

/**
 * Configuration for pagination property names used by different APIs
 */
interface PaginationConfig {
  /** Name of the request parameter for cursor (e.g., 'start' for Core API, 'after' for Progress/Activity API) */
  requestParam: string;

  /** Name of the response property for next cursor (e.g., 'next' for Core API, 'after' for Progress/Activity API) */
  responseCursor: string;
}

/**
 * Configuration object for exporting all paginated data
 */
interface ExportAllConfig<TParams, TData, TResult> {
  /** Parameters to pass to the fetch function */
  fetchParams: TParams;

  /** Function to fetch a single page from the API */
  fetchPage: FetchPageFunction<TParams, TData>;

  /** Function to transform raw API data to output format */
  transform: TransformFunction<TData, TResult>;

  /**
   * Optional callback for progress updates.
   * Note: Total pages is unknown for cursor-based pagination.
   */
  onProgress?: (currentPage: number) => void;

  /**
   * Optional abort signal for cancellation support.
   * When aborted, the export will stop and return null.
   */
  signal?: AbortSignal;

  /**
   * Optional pagination configuration for different API patterns.
   * Defaults to Core API pattern: { requestParam: 'start', responseCursor: 'next' }
   * For Progress/Activity API use: { requestParam: 'after', responseCursor: 'after' }
   */
  paginationConfig?: Partial<PaginationConfig>;
}

/**
 * Return value from useExportAllPaginated hook
 */
interface ExportAllPaginatedHook<TParams, TData, TResult> {
  /**
   * Function to export all pages from a paginated API.
   * @returns Array of results if successful, null if cancelled
   * @throws Error if export fails
   */
  exportAll: (
    config: ExportAllConfig<TParams, TData, TResult>
  ) => Promise<TResult[] | null>;
}

/**
 * Hook to export all paginated data with cancellation support.
 * Uses direct API calls instead of Redux thunk to avoid triggering store updates and re-renders.
 */
export function useExportAllPaginated<
  TParams,
  TData,
  TResult
>(): ExportAllPaginatedHook<TParams, TData, TResult> {
  const exportAll = useCallback(
    async (
      config: ExportAllConfig<TParams, TData, TResult>
    ): Promise<TResult[] | null> => {
      const {
        fetchParams,
        fetchPage,
        transform,
        onProgress,
        signal,
        paginationConfig,
      } = config;

      // Apply defaults for Core API pattern
      const pagination: PaginationConfig = {
        requestParam: paginationConfig?.requestParam ?? "start",
        responseCursor: paginationConfig?.responseCursor ?? "next",
      };

      const results: TResult[] = [];
      let cursor: string | undefined;
      let page = 0;

      do {
        // Check for cancellation before each request
        if (signal?.aborted) {
          return null;
        }

        page++;

        const response = await fetchPage({
          ...fetchParams,
          [pagination.requestParam]: cursor,
        });

        // Check for cancellation after request completes
        if (signal?.aborted) {
          return null;
        }

        results.push(...transform(response.data));

        // Extract next cursor from response
        const nextCursor = response[pagination.responseCursor];
        cursor = typeof nextCursor === "string" ? nextCursor : undefined;

        onProgress?.(page);

        // Rate limit between requests if there's a next page
        if (cursor) {
          await delay(RATE_LIMIT_DELAY_MS);
        }
      } while (cursor);

      return results;
    },
    []
  );

  return { exportAll };
}
